import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Package, Search } from "lucide-react";
import { Layout } from "@/components/Layout";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { clientsApi, scheduledOrderRulesApi, ScheduledOrderRuleProduct } from "@/lib/api";

const categoryLabels: Record<string, string> = {
  ImmediateAndSecondSemester: "2º semestre",
  SecondSemesterOnly: "2º semestre",
  ExamScheduled: "Avaliação programada",
  ImmediateAndExam: "Avaliação: fatura + programa",
  Unresolved: "Configuração pendente",
};

const badgeVariant = (item: ScheduledOrderRuleProduct) =>
  item.category === "ExamScheduled" || item.category === "ImmediateAndExam" ? "secondary" : item.category === "Unresolved" ? "destructive" : "default";

const categoryGroup = (category: string) => {
  if (category === "ImmediateAndSecondSemester" || category === "SecondSemesterOnly") {
    return { key: "second-semester", label: "2º semestre", description: "Produtos destinados ao pedido programado do segundo semestre" };
  }
  if (category === "ExamScheduled" || category === "ImmediateAndExam") {
    return { key: "exam", label: "Avaliação programada", description: "Avaliações e simulados organizados por remessa (Avaliações Nacionais e Simulado ENEM também são faturados no pedido)" };
  }
  return { key: "unresolved", label: "Configuração pendente", description: "Produtos que precisam de revisão na regra de programação" };
};

export default function ScheduledOrderRules() {
  const [cycleYear, setCycleYear] = useState<number | null>(null);
  const [search, setSearch] = useState("");
  const [openCategories, setOpenCategories] = useState<string[]>([]);
  const { data: commercialCycles = [], isLoading: cyclesLoading } = useQuery({
    queryKey: ["commercial-cycles-order"],
    queryFn: async () => {
      const response = await clientsApi.getCycles();
      return response.cycles || [];
    },
    staleTime: 5 * 60 * 1000,
  });
  const availableCycleYears = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return commercialCycles
      .filter(cycle => cycle.status === "Open"
        && cycle.year >= 2027
        && (cycle.year === currentYear || cycle.year === currentYear + 1))
      .map(cycle => cycle.year)
      .sort((a, b) => a - b)
      .slice(0, 2);
  }, [commercialCycles]);
  useEffect(() => {
    if (availableCycleYears.length === 0) return;
    if (cycleYear == null || !availableCycleYears.includes(cycleYear)) {
      setCycleYear(availableCycleYears[0]);
    }
  }, [availableCycleYears, cycleYear]);
  const { data, isLoading, isError } = useQuery({
    queryKey: ["scheduled-order-rule-products", cycleYear],
    queryFn: () => scheduledOrderRulesApi.getProducts(cycleYear!),
    enabled: cycleYear != null,
  });
  const products = useMemo(() => data?.data ?? [], [data]);
  const filtered = useMemo(() => {
    const term = search.trim().toLocaleLowerCase("pt-BR");
    if (!term) return products;
    return products.filter(item => [
      item.productCode, item.productSku, item.productDescription,
      item.targetProductCode, item.targetProductSku, item.targetProductDescription,
      item.segmentName, item.seriesName, item.brandName,
    ].some(value => value?.toLocaleLowerCase("pt-BR").includes(term)));
  }, [products, search]);
  const groupedProducts = useMemo(() => {
    const groups = new Map<string, {
      key: string;
      label: string;
      description: string;
      products: ScheduledOrderRuleProduct[];
    }>();

    filtered.forEach(product => {
      const group = categoryGroup(product.category);
      const current = groups.get(group.key) ?? { ...group, products: [] };
      current.products.push(product);
      groups.set(group.key, current);
    });

    const order = ["second-semester", "exam", "unresolved"];
    return Array.from(groups.values()).sort((left, right) =>
      order.indexOf(left.key) - order.indexOf(right.key));
  }, [filtered]);

  useEffect(() => {
    setOpenCategories(groupedProducts.map(group => group.key));
  }, [cycleYear, groupedProducts]);

  const renderProductsTable = (items: ScheduledOrderRuleProduct[]) => (
    <Table>
      <TableHeader><TableRow>
        <TableHead>Produto de origem</TableHead><TableHead>Classificação</TableHead>
        <TableHead>Produto programado</TableHead><TableHead>Contexto</TableHead>
      </TableRow></TableHeader>
      <TableBody>{items.map(item => <TableRow key={`${item.productId}-${item.category}-${item.productSku}`}>
        <TableCell><div className="font-medium">{item.productDescription}</div><div className="text-xs text-muted-foreground">{item.productCode} · SKU {item.productSku || "—"}</div></TableCell>
        <TableCell><Badge variant={badgeVariant(item)}>{categoryLabels[item.category] ?? item.category}</Badge></TableCell>
        <TableCell><div className="font-medium">{item.targetProductDescription || "—"}</div>{item.targetProductId && <div className="text-xs text-muted-foreground">{item.targetProductCode} · SKU {item.targetProductSku || "—"}</div>}</TableCell>
        <TableCell><div>{item.segmentName || "Sem segmento"} · {item.seriesName || "Sem série"}</div><div className="text-xs text-muted-foreground">{item.brandName || "Sem marca"} · {item.materialUseName || "Sem utilização"}</div></TableCell>
      </TableRow>)}</TableBody>
    </Table>
  );

  return (
    <Layout title="Produtos Programados">
      <div className="space-y-6">
        <p className="text-sm text-muted-foreground">
          Produtos classificados pelo mesmo motor do backend usado na criação dos pedidos.
        </p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {cyclesLoading
            ? <><Skeleton className="h-20" /><Skeleton className="h-20" /></>
            : availableCycleYears.map(year => {
              const isSelected = cycleYear === year;
              return <button key={year} type="button" onClick={() => setCycleYear(year)} disabled={isSelected}
                className={`relative rounded-lg border-2 p-4 text-left transition-colors ${isSelected
                  ? "border-primary bg-primary/5 cursor-default"
                  : "border-gray-300 bg-gray-50 hover:border-primary/50 hover:bg-primary/5 cursor-pointer"}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Package className={`h-5 w-5 ${isSelected ? "text-primary" : "text-gray-400"}`} />
                    <span className={`font-semibold ${isSelected ? "text-primary" : "text-gray-500"}`}>Ciclo {year}</span>
                  </div>
                  {isSelected && <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">● Ativo</span>}
                </div>
                <p className="mt-1 text-xs text-muted-foreground">{isSelected ? "Produtos exibidos abaixo" : "Clique para trocar para este ciclo"}</p>
              </button>;
            })}
        </div>
        <div className="flex flex-wrap gap-3">
          <label className="relative min-w-[280px] flex-1">
            <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input className="pl-9" placeholder="Buscar produto, SKU, segmento ou série" value={search}
              onChange={event => setSearch(event.target.value)} />
          </label>
        </div>
        <Card>
          <CardHeader className="border-b bg-muted/20">
            <CardTitle className="flex items-center justify-between text-base">
              Produtos programados{cycleYear ? ` de ${cycleYear}` : ""}
              <Badge variant="outline">{filtered.length} produtos</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {isLoading ? <div className="space-y-3 p-6"><Skeleton className="h-10" /><Skeleton className="h-10" /></div>
              : isError ? <div className="p-8 text-center text-sm text-destructive">Não foi possível consultar o motor de produtos programados.</div>
              : filtered.length === 0 ? <div className="p-8 text-center text-sm text-muted-foreground">Nenhum produto programado encontrado para o ciclo.</div>
              : <Accordion type="multiple" value={openCategories} onValueChange={setOpenCategories}>
                {groupedProducts.map(group => <AccordionItem key={group.key} value={group.key} className="border-b last:border-b-0">
                  <AccordionTrigger className="px-6 py-4 text-left hover:bg-muted/30 hover:no-underline">
                    <div className="flex flex-1 items-center justify-between gap-4 pr-4">
                      <div>
                        <div className="font-semibold">{group.label}</div>
                        <div className="mt-0.5 text-xs font-normal text-muted-foreground">{group.description}</div>
                      </div>
                      <Badge variant="outline">{group.products.length} produtos</Badge>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pb-0">
                    {renderProductsTable(group.products)}
                  </AccordionContent>
                </AccordionItem>)}
              </Accordion>}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
